// Simplified Style Presets using 6-Color System

// Color utility functions
const hexToRgb = (hex) => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return { r, g, b };
};

// Helper function to lighten colors
const lightenColor = (hex, factor = 1.2) => {
  try {
    const { r, g, b } = hexToRgb(hex);
    const hsl = rgbToHsl(r, g, b);
    hsl.l = Math.min(1, hsl.l * factor);
    const rgb = hslToRgb(hsl.h, hsl.s, hsl.l);
    return rgbToHex(rgb.r, rgb.g, rgb.b);
  } catch (error) {
    console.error('Error lightening color:', error);
    return hex;
  }
};

const rgbToHex = (r, g, b) => {
  // Ensure values are numbers and within valid range (0-255)
  const safeValue = (val) => {
    const num = Number(val);
    if (isNaN(num)) return 0;
    return Math.max(0, Math.min(255, Math.round(num)));
  };

  try {
    const values = [r, g, b].map(safeValue);
    return `#${values.map(x => x.toString(16).padStart(2, '0')).join('')}`;
  } catch (error) {
    console.error('Error in rgbToHex:', error);
    return '#000000'; // Fallback to black if conversion fails
  }
};

// Calculate relative luminance for contrast calculations
const getLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Calculate contrast ratio between two colors
const getContrastRatio = (color1, color2) => {
  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

// Adjust color to meet minimum contrast ratio
const ensureContrast = (color, backgroundColor, minContrast = 4.5) => {
  let { r, g, b } = hexToRgb(color);
  const currentContrast = getContrastRatio(color, backgroundColor);

  if (currentContrast >= minContrast) return color;

  const targetLuminance = getLuminance(backgroundColor);
  const step = targetLuminance > 0.5 ? -5 : 5;

  // Cap iterations to prevent infinite loop when contrast target is unreachable
  // (e.g. RGB values clamped at 0 or 255 but contrast still below target)
  for (let i = 0; i < 52; i++) {
    if (getContrastRatio(rgbToHex(r, g, b), backgroundColor) >= minContrast) break;
    const nr = Math.max(0, Math.min(255, r + step));
    const ng = Math.max(0, Math.min(255, g + step));
    const nb = Math.max(0, Math.min(255, b + step));
    // Stop early if values aren't changing (clamped at boundary)
    if (nr === r && ng === g && nb === b) break;
    r = nr; g = ng; b = nb;
  }

  return rgbToHex(r, g, b);
};

// Improved darken function with luminance preservation
const darkenColor = (hex, factor = 0.8) => {
  const { r, g, b } = hexToRgb(hex);
  const hsl = rgbToHsl(r, g, b);
  hsl.l = Math.max(0, hsl.l * factor);
  const rgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
};

// HSL conversion utilities (for better color manipulation)
const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h, s, l };
};

const hslToRgb = (h, s, l) => {
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

// Derive a full 7-color palette from 3 user-chosen colors.
// brandColor  → background + surface (the canvas)
// accentColor → primaryAction + primary/titles + secondaryAction (the pop)
// textColor   → text + heading text + button text auto-contrast (readability)
export const deriveBrandAccentPalette = (brandColor, accentColor, textColor) => {
  try {
    const brandLum = getLuminance(brandColor);
    const accentLum = getLuminance(accentColor);

    // Background and surface use the brand colour directly. Games run on top of a
    // background image, so a separate offset surface adds noise rather than clarity.
    // Frames/overlays/patterns provide visual structure on top of the flat colour.
    const background = brandLum > 0.7 ? darkenColor(brandColor, 0.95) : brandColor;
    const surface = background;

    // Text: use exactly what the user picked. Only auto-derive if not provided.
    const text = textColor || (() => {
      const lightText = '#ECF0F1';
      const darkText = '#374151';
      const surfaceLum = getLuminance(surface);
      if (surfaceLum < 0.35) return lightText;
      if (surfaceLum > 0.65) return darkText;
      return getContrastRatio(lightText, surface) > getContrastRatio(darkText, surface)
        ? lightText : darkText;
    })();

    // Primary (frames, borders, accents): always derived from accent
    const primary = ensureContrast(accentColor, surface, 3.0);

    // PrimaryAction (buttons): accent directly
    const primaryAction = accentColor;

    // SecondaryAction: hue-shift accent by +25 degrees
    const accentRgb = hexToRgb(accentColor);
    const accentHsl = rgbToHsl(accentRgb.r, accentRgb.g, accentRgb.b);
    const secHue = (accentHsl.h + 25 / 360) % 1;
    const secLightness = Math.max(0.35, Math.min(0.65, accentHsl.l));
    const secRgb = hslToRgb(secHue, accentHsl.s, secLightness);
    const secondaryAction = rgbToHex(secRgb.r, secRgb.g, secRgb.b);

    // ButtonText: auto-derive for contrast on accent buttons
    const buttonText = accentLum > 0.4 ? '#1F2937' : '#FFFFFF';

    return {
      background,
      surface,
      primary,
      primaryAction,
      secondaryAction,
      text,
      buttonText,
    };
  } catch (error) {
    console.error('Error in deriveBrandAccentPalette:', error);
    return {
      background: brandColor || '#2C3E50',
      surface: '#34495E',
      primary: textColor || '#F1C40F',
      primaryAction: accentColor || '#E74C3C',
      secondaryAction: '#F97316',
      text: textColor || '#ECF0F1',
      buttonText: '#FFFFFF',
    };
  }
};

// Derive a full 7-color palette from just 3 user-chosen colors.
// brandColor   → primaryAction (CTA buttons, player character)
// highlightColor → primary (titles, collectibles, accents)
// backgroundColor → background (game / screen background)
// The remaining 4 (surface, text, secondaryAction, buttonText) are auto-derived.
export const deriveFullPalette = (brandColor, highlightColor, backgroundColor) => {
  try {
    const bgLum = getLuminance(backgroundColor);

    // surface: offset from background to create card/overlay layer
    let surface;
    if (bgLum < 0.2) {
      surface = lightenColor(backgroundColor, 1.3);
    } else if (bgLum > 0.7) {
      surface = darkenColor(backgroundColor, 0.92);
    } else {
      surface = lightenColor(backgroundColor, 1.15);
    }

    // text: ensure readable body text against the background
    const lightText = '#ECF0F1';
    const darkText = '#374151';
    let text;
    if (bgLum < 0.35) {
      text = lightText;
    } else if (bgLum > 0.65) {
      text = darkText;
    } else {
      text = getContrastRatio(lightText, backgroundColor) > getContrastRatio(darkText, backgroundColor)
        ? lightText
        : darkText;
    }

    // Ensure primary (headline color) is readable against the card surface
    // WCAG AA large text requires 3:1 minimum contrast ratio
    const primary = ensureContrast(highlightColor, surface, 3.0);

    // secondaryAction: hue-shift the brand color +25° for a complementary accent
    const brandRgb = hexToRgb(brandColor);
    const brandHsl = rgbToHsl(brandRgb.r, brandRgb.g, brandRgb.b);
    const secHue = (brandHsl.h + 25 / 360) % 1;
    const secLightness = Math.max(0.35, Math.min(0.65, brandHsl.l));
    const secRgb = hslToRgb(secHue, brandHsl.s, secLightness);
    const secondaryAction = rgbToHex(secRgb.r, secRgb.g, secRgb.b);

    // buttonText: white on dark brand colors, dark on light
    const buttonText = getLuminance(brandColor) > 0.4 ? '#1F2937' : '#FFFFFF';

    return {
      background: backgroundColor,
      surface,
      primary,
      primaryAction: brandColor,
      secondaryAction,
      text,
      buttonText
    };
  } catch (error) {
    console.error('Error in deriveFullPalette:', error);
    return {
      background: backgroundColor || '#2C3E50',
      surface: '#34495E',
      primary: highlightColor || '#F1C40F',
      primaryAction: brandColor || '#E74C3C',
      secondaryAction: '#F97316',
      text: '#ECF0F1',
      buttonText: '#FFFFFF'
    };
  }
};

// Enhanced function to create a complete theme from the 6 core colors
export const generateCustomTheme = (colors, font, gameType) => {
  try {
    const { background, surface, primary, primaryAction, secondaryAction, text, buttonText } = colors;
    

    
    // Ensure we have all 7 required colors with fallbacks
    const safeColors = {
      background: background ?? '#4B5563',       // Dark base backgrounds
      surface: surface ?? '#FFFFFF',             // Cards, surfaces, content areas  
      primary: primary ?? '#F1C40F',             // Headlines, highlights, accents
      primaryAction: primaryAction ?? '#7C3AED', // CTA buttons (priority) or share buttons
      secondaryAction: secondaryAction ?? '#F97316', // Regular actions or secondary CTAs
      text: text ?? '#4B5563',                  // Text content on light surfaces
      buttonText: buttonText ?? '#FFFFFF'        // Text on buttons/action elements
    };

    // Create dynamic variations for gradients
    const darkerBackground = darkenColor(safeColors.background, 0.9);



    // Simplified theme structure - games handle their own styling now
    const theme = {
      // Store the core 6 colors for components that want direct access
      customColors: safeColors,
      
      // Minimal structure for backward compatibility
      colors: {
        primary: safeColors.primary,
        accent: safeColors.primaryAction,
        background: {
          gradient: {
            start: safeColors.background,
            end: darkerBackground
          }
        },
        cardBackground: safeColors.surface,
        cardOpacity: 0.95,
        titleText: safeColors.text,
        subtitleText: safeColors.text,
        buttonBackground: safeColors.primaryAction,
        buttonText: safeColors.buttonText,
        buttonOpacity: 1
      },
      
      // Typography settings
      typography: {
        primaryFont: font || 'Inter'
      }
    };

    return theme;
    
  } catch (error) {
    console.error('Error generating custom theme:', error);
    
    // Return safe fallback theme
    const fallbackColors = {
      background: '#4B5563',
      surface: '#FFFFFF',
      primary: '#F1C40F',
      text: '#4B5563',
      primaryAction: '#7C3AED',
      secondaryAction: '#F97316',
      buttonText: '#FFFFFF'
    };
    
    return {
      customColors: fallbackColors,
      colors: {
        primary: fallbackColors.primary,
        accent: fallbackColors.primaryAction,
        background: { gradient: { start: fallbackColors.background, end: '#374151' } },
        cardBackground: fallbackColors.surface,
        cardOpacity: 0.95,
        titleText: fallbackColors.text,
        subtitleText: fallbackColors.text,
        buttonBackground: fallbackColors.primaryAction,
        buttonText: fallbackColors.buttonText
      },
      typography: { primaryFont: font || 'Inter' }
    };
  }
};

// Simplified getStylePresets using 6-color system
/**
 * Single source of truth for the bundled design presets. Each entry is a
 * complete look: 3 base colors + font + structural style props (frame /
 * overlay / layout / motion). The AI brand wizard picks one preset by slug
 * and overlays the brand's actual colors on top, so the resulting design
 * stays coherent regardless of brand palette.
 *
 * `description` is human-readable mood guidance used in the AI prompt to
 * help the model pick the right preset for a given brand.
 */
// PRESETS — single source of truth for the 10 design presets used by both
// the user-facing theme selector (StyleLibraryPicker) and the AI brand
// wizard (/generate-theme). StyleLibraryPicker re-exports from here.
// Descriptions guide the AI's choice; "ONLY for…" copy keeps it from
// over-picking themed presets and biases toward SmilesFlow Default.
export const PRESETS = [
  {
    id: 'preset-smilesflow-default',
    name: 'SmilesFlow Default',
    colors: { background: '#2E3D4A', primaryAction: '#E56A54', text: '#FFFFFF' },
    font: 'Bungee', frameStyle: 'sticker', overlay: null, pattern: null, layout: 'stack', motionStyle: 'bounce',
    description: "SmilesFlow Default. PREFERRED CHOICE for most brands. Bold sticker arcade card with playful coral CTA. Friendly, fun, works across industries. Pick this whenever the brand doesn't clearly demand one of the more specific themes.",
  },
  {
    id: 'preset-comic',
    name: 'Comic Book',
    colors: { background: '#FFD700', primaryAction: '#FF0000', text: '#000000' },
    font: 'Bangers', frameStyle: 'pixel', overlay: null, pattern: 'dots', layout: 'stack', motionStyle: 'bounce',
    description: "Comic Book. Yellow + red + dot pattern, pixel frame. ONLY for explicitly playful, kid-friendly, or comic-themed brands.",
  },
  {
    id: 'preset-atari',
    name: 'Atari',
    colors: { background: '#000000', primaryAction: '#FF6600', text: '#FFFFFF' },
    font: 'VT323', frameStyle: 'crt', overlay: 'scanlines', pattern: null, layout: 'stack', motionStyle: 'bounce',
    description: "Atari. Pure black + orange CRT scanlines, pixel font. ONLY for retro gaming, vintage tech, or 80s arcade themed brands.",
  },
  {
    id: 'preset-synthwave',
    name: 'Synthwave',
    colors: { background: '#0F0326', primaryAction: '#FF0080', text: '#E6E6FA' },
    font: 'Orbitron', frameStyle: 'crt', overlay: 'scanlines', pattern: null, layout: 'stack', motionStyle: 'slideUp',
    description: "Synthwave. Deep purple + hot pink + scanlines, futurist Orbitron font. ONLY for gaming, esports, nightclubs, EDM events, or cyberpunk aesthetic.",
  },
  {
    id: 'preset-corporate',
    name: 'Corporate',
    colors: { background: '#F8FAFC', primaryAction: '#2563EB', text: '#475569' },
    font: 'Inter', frameStyle: 'clean', overlay: null, pattern: null, layout: 'stack', motionStyle: 'slideUp',
    description: "Corporate. Clean light background + corporate blue. ONLY for enterprise, financial services, law firms, or professional services with a stiff conservative aesthetic.",
  },
  {
    id: 'preset-dark-modern',
    name: 'Dark Modern',
    colors: { background: '#0F0F23', primaryAction: '#3B82F6', text: '#94A3B8' },
    font: 'Inter', frameStyle: 'clean', overlay: null, pattern: 'grid', layout: 'stack', motionStyle: 'slideUp',
    description: "Dark Modern. Deep navy + cool blue, Inter font, grid pattern. ONLY for tech, fintech, B2B SaaS with a clearly dark UI.",
  },
  {
    id: 'preset-gold',
    name: 'Black & Gold',
    colors: { background: '#0A0A0A', primaryAction: '#DAA520', text: '#F5F5DC' },
    font: 'Cinzel', frameStyle: 'bordered', overlay: 'sparkles', pattern: null, layout: 'centered', motionStyle: 'slideUp',
    description: "Black & Gold. Black background, gold accents, sparkles, Cinzel serif. ONLY for luxury, fine dining, high-end events, or premium brands.",
  },
  {
    id: 'preset-sunset',
    name: 'Sunset Vibes',
    colors: { background: '#7C2D12', primaryAction: '#FB923C', text: '#FED7AA' },
    font: 'Poppins', frameStyle: 'gradient', overlay: null, pattern: 'topographic', layout: 'stack', motionStyle: 'fade',
    description: "Sunset Vibes. Warm rust + orange gradient + topographic pattern. ONLY for hospitality, travel, food/drink, or warm summer brands.",
  },
  {
    id: 'preset-ocean',
    name: 'Ocean Depths',
    colors: { background: '#0F766E', primaryAction: '#06B6D4', text: '#A7F3D0' },
    font: 'Raleway', frameStyle: 'glass', overlay: 'bubbles', pattern: 'topographic', layout: 'stack', motionStyle: 'fade',
    description: "Ocean Depths. Teal + cyan + bubbles. ONLY for travel, watersports, coastal hospitality, or marine brands.",
  },
  {
    id: 'preset-christmas',
    name: 'Christmas',
    colors: { background: '#0F4229', primaryAction: '#C41E3A', text: '#FFFAF0' },
    font: 'Mountains of Christmas', frameStyle: 'bordered', overlay: 'snowfall', pattern: null, layout: 'stack', motionStyle: 'bounce',
    description: "Christmas. Forest green + Christmas red + snowfall. ONLY for explicitly festive / December campaigns.",
  },
];

// PRESET_DEFINITIONS — keyed by id, reshaped into the { colors, font, style }
// shape consumed by the generate-theme client code. Same data, different
// shape; iterate via PRESETS, look up via PRESET_DEFINITIONS[id].
export const PRESET_DEFINITIONS = Object.freeze(
  PRESETS.reduce((acc, p) => {
    acc[p.id] = {
      name: p.name,
      colors: p.colors,
      font: p.font,
      style: {
        frameStyle: p.frameStyle,
        overlay: p.overlay,
        pattern: p.pattern,
        layout: p.layout,
        motionStyle: p.motionStyle,
      },
      description: p.description,
    };
    return acc;
  }, {})
);

/** Slugs only (for AI prompt validation). */
export const PRESET_SLUGS = Object.keys(PRESET_DEFINITIONS);

export const getStylePresets = (gameTypeColor, businessProfile, gameType) => {
  try {
    if (typeof gameTypeColor !== 'string') {
      gameTypeColor = '#4F46E5';
    }

    if (typeof gameType !== 'string') {
      gameType = '';
    }

    // 3-color preset definitions: background (brand), primaryAction (accent/buttons), text (body)
    // Full 7-color palettes are derived via deriveBrandAccentPalette at consumption time.
    // Each preset is a complete design opinion: font + frame + overlay + layout
    // + motion. The AI brand wizard picks a preset by name and overlays the
    // brand's actual colors on top, so the resulting theme is always coherent.
    const presetDefinitions = PRESET_DEFINITIONS;

    // Derive full 7-color palettes from 3 base colours, then generate themes
    const presets = {};
    Object.entries(presetDefinitions).forEach(([key, definition]) => {
      const { background, primaryAction, text } = definition.colors;
      const fullColors = deriveBrandAccentPalette(background, primaryAction, text);
      presets[key] = generateCustomTheme(fullColors, definition.font, gameType);
    });

    return presets;

  } catch (error) {
    console.error('Error in getStylePresets:', error);
    
    // Return minimal fallback
    return {
      default: generateCustomTheme({
        background: '#F8FAFC',
        surface: '#FFFFFF',
        primary: '#4F46E5',
        primaryAction: '#0369A1',
        secondaryAction: '#F97316',
        text: '#1F2937',
        buttonText: '#FFFFFF'
      }, 'Inter', gameType)
    };
  }
}; 